import React, { useRef, useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { FileText, Download, Printer, ArrowLeft, CheckCircle, Copy, Check } from 'lucide-react';
import jsPDF from 'jspdf';

function EthicsReport() {
  const { dataE, dataA, switchWizard, goToStep, completeWizard, wizardEComplete } = useWizard();
  
  useEffect(() => {
    if (!wizardEComplete) {
      completeWizard('E');
    }
  }, [wizardEComplete, completeWizard]);
  const reportRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const getLabel = (value, options) => {
    const option = options.find(o => o.id === value || o.value === value);
    return option?.label || value || 'Not specified';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const fundingSources = [
    { id: 'internal', label: 'Internal / Institutional Funding' },
    { id: 'external_grant', label: 'External Research Grant' },
    { id: 'industry', label: 'Industry / Commercial Sponsor' },
    { id: 'government', label: 'Government Funding' },
    { id: 'charity', label: 'Charity / Foundation' },
    { id: 'self_funded', label: 'Self-Funded' },
    { id: 'none', label: 'No Funding Required' },
    { id: 'other', label: 'Other' }
  ];

  const retentionPeriods = [
    { id: 'during_study', label: 'During study only' },
    { id: '1_year', label: '1 year after study completion' },
    { id: '3_years', label: '3 years after study completion' },
    { id: '5_years', label: '5 years after study completion' },
    { id: '10_years', label: '10 years after study completion' },
    { id: 'indefinite', label: 'Indefinitely' },
    { id: 'other', label: 'Other' }
  ];

  const roles = [
    { id: 'staff', label: 'Staff Member' },
    { id: 'postdoc', label: 'Postdoctoral Researcher' },
    { id: 'phd', label: 'PhD Student' },
    { id: 'masters', label: 'Masters Student' },
    { id: 'undergraduate', label: 'Undergraduate Student' },
    { id: 'external', label: 'External Researcher' }
  ];

  const generatePDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = margin;

    const addHeader = () => {
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Ethics Application', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(dataE.projectSynopsis.title || 'Untitled Project', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;
    };

    const checkNewPage = (neededSpace = 20) => {
      if (yPos + neededSpace > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
      }
    };

    const addSectionHeader = (title) => {
      checkNewPage(15);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, margin, yPos);
      yPos += 8;
    };

    const addField = (label, value) => {
      checkNewPage(15);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(label + ':', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(value || 'Not specified', contentWidth);
      lines.forEach(line => {
        checkNewPage(6);
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      yPos += 3;
    };

    addHeader();

    addSectionHeader('1. Research Team');
    const pi = dataE.researchTeam.principalInvestigator;
    addField('Principal Investigator', `${pi.firstName} ${pi.lastName}`);
    addField('Email', pi.email);
    addField('Institution', pi.institution);
    addField('Faculty/Department', `${pi.faculty || ''} / ${pi.department || ''}`);
    addField('Role', getLabel(pi.role, roles));

    if (dataE.researchTeam.supervisors.length > 0) {
      addField('Supervisors', dataE.researchTeam.supervisors
        .map(s => `${s.firstName} ${s.lastName} (${s.email})`)
        .join('; '));
    }

    addSectionHeader('2. Project Synopsis');
    addField('Project Title', dataE.projectSynopsis.title);
    addField('Project Dates', `${formatDate(dataE.projectSynopsis.startDate)} - ${formatDate(dataE.projectSynopsis.endDate)}`);
    addField('Ethics Period', `${formatDate(dataE.projectSynopsis.ethicsStartDate)} - ${formatDate(dataE.projectSynopsis.ethicsEndDate)}`);
    addField('Funding Source', getLabel(dataE.projectSynopsis.fundingSource, fundingSources));
    addField('Project Summary', dataE.projectSynopsis.summary);
    addField('Research Questions', dataE.projectSynopsis.researchQuestions);
    addField('Methodology', dataE.projectSynopsis.methodology);

    addSectionHeader('3. Participants');
    addField('Description', dataE.participants.description);
    addField('Selection Method', dataE.participants.selectionMethod);
    addField('Sample Size Justification', dataE.participants.sampleSizeJustification);
    addField('Inclusion Criteria', dataE.participants.inclusionCriteria);
    addField('Exclusion Criteria', dataE.participants.exclusionCriteria);
    addField('Involves Vulnerable Groups', dataE.participants.vulnerableGroups.includes ? 'Yes' : 'No');
    if (dataE.participants.vulnerableGroups.includes) {
      addField('Vulnerable Groups', dataE.participants.vulnerableGroups.types?.join(', '));
      addField('Justification & Safeguards', dataE.participants.vulnerableGroups.justification);
    }

    addSectionHeader('4. Recruitment & Consent');
    addField('Recruitment Methods', dataE.recruitment.methods?.join(', '));
    addField('Initial Contact Information', dataE.recruitment.initialInfo);
    addField('Consent Process', dataE.recruitment.consentProcess);
    addField('Withdrawal Procedure', dataE.recruitment.withdrawalProcedure);
    addField('Debrief Procedure', dataE.recruitment.debriefProcedure);

    addSectionHeader('5. Data Collection');
    addField('Collection Methods', dataE.dataCollection.methods?.join(', '));
    addField('Research Instruments', dataE.dataCollection.instruments);
    addField('Pilot Testing', dataE.dataCollection.pilotTesting);

    addSectionHeader('6. Data Management');
    addField('Types of Personal Data', dataE.dataManagement.dataTypes?.join(', '));
    addField('Recording Method', dataE.dataManagement.recordingMethod);
    addField('Anonymization Method', dataE.dataManagement.anonymizationMethod);
    addField('Storage Locations', dataE.dataManagement.storageLocations?.join(', '));
    addField('Access Control', dataE.dataManagement.accessControl);
    addField('Security Measures', dataE.dataManagement.securityMeasures);
    addField('Retention Period', getLabel(dataE.dataManagement.retentionPeriod, retentionPeriods));
    addField('Disposal Method', dataE.dataManagement.disposalMethod);
    addField('GDPR Compliant', dataE.dataManagement.gdprCompliant ? 'Yes' : 'No');

    addSectionHeader('7. Risk Assessment');
    addField('Participant Risks', dataE.riskAssessment.participantRisks?.join(', '));
    addField('Mitigation Measures', dataE.riskAssessment.participantMitigations);
    addField('Researcher Risks', dataE.riskAssessment.researcherRisks);
    addField('Researcher Mitigations', dataE.riskAssessment.researcherMitigations);
    addField('Incident Response', dataE.riskAssessment.incidentResponse);

    addSectionHeader('8. Special Considerations');
    if (dataE.specialConsiderations.involvesMinors) {
      addField('Involves Children/Young People', dataE.specialConsiderations.minorsDetails);
    }
    if (dataE.specialConsiderations.involvesHumanTissue) {
      addField('Involves Human Tissue', dataE.specialConsiderations.tissueDetails);
    }
    if (dataE.specialConsiderations.involvesIncentives) {
      addField('Participant Incentives', dataE.specialConsiderations.incentivesDetails);
    }

    addSectionHeader('9. Dissemination');
    addField('Dissemination Methods', dataE.dissemination.methods?.join(', '));
    addField('Data Sharing Plans', dataE.dissemination.dataSharing);
    addField('Embargo Requirements', dataE.dissemination.embargoPlans);
    addField('Participant Feedback', dataE.dissemination.participantFeedback);

    addSectionHeader('10. Declarations');
    addField('Accuracy Confirmed', dataE.declarations.confirmedAccuracy ? 'Yes' : 'No');
    addField('GDPR Compliance Confirmed', dataE.declarations.confirmedGDPR ? 'Yes' : 'No');
    addField('Training Confirmed', dataE.declarations.confirmedTraining ? 'Yes' : 'No');
    addField('Additional Comments', dataE.declarations.additionalComments);

    checkNewPage(20);
    yPos += 10;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text(`Generated on ${new Date().toLocaleDateString('en-GB')} using Research Methodology Wizard`, 
             pageWidth / 2, yPos, { align: 'center' });

    pdf.save('ethics-application.pdf');
  };

  const handlePrint = () => {
    window.print();
  };

  const generatePlainText = () => {
    const lines = [];
    const addLine = (text) => lines.push(text);
    const addField = (label, value) => {
      lines.push(`${label}: ${value || 'Not specified'}`);
    };
    const addSection = (title) => {
      lines.push('');
      lines.push(`=== ${title} ===`);
    };

    addLine('ETHICS APPLICATION');
    addLine(dataE.projectSynopsis.title || 'Untitled Project');
    addLine('='.repeat(50));

    addSection('1. Research Team');
    const pi = dataE.researchTeam.principalInvestigator;
    addField('Principal Investigator', `${pi.firstName} ${pi.lastName}`);
    addField('Email', pi.email);
    addField('Institution', pi.institution);
    addField('Faculty/Department', `${pi.faculty || ''} / ${pi.department || ''}`);
    addField('Role', getLabel(pi.role, roles));
    if (dataE.researchTeam.supervisors.length > 0) {
      addField('Supervisors', dataE.researchTeam.supervisors.map(s => `${s.firstName} ${s.lastName} (${s.email})`).join('; '));
    }

    addSection('2. Project Synopsis');
    addField('Project Title', dataE.projectSynopsis.title);
    addField('Project Dates', `${formatDate(dataE.projectSynopsis.startDate)} - ${formatDate(dataE.projectSynopsis.endDate)}`);
    addField('Ethics Period', `${formatDate(dataE.projectSynopsis.ethicsStartDate)} - ${formatDate(dataE.projectSynopsis.ethicsEndDate)}`);
    addField('Funding Source', getLabel(dataE.projectSynopsis.fundingSource, fundingSources));
    addField('Project Summary', dataE.projectSynopsis.summary);
    addField('Research Questions', dataE.projectSynopsis.researchQuestions);
    addField('Methodology', dataE.projectSynopsis.methodology);

    addSection('3. Participants');
    addField('Description', dataE.participants.description);
    addField('Selection Method', dataE.participants.selectionMethod);
    addField('Sample Size Justification', dataE.participants.sampleSizeJustification);
    addField('Inclusion Criteria', dataE.participants.inclusionCriteria);
    addField('Exclusion Criteria', dataE.participants.exclusionCriteria);
    addField('Involves Vulnerable Groups', dataE.participants.vulnerableGroups.includes ? 'Yes' : 'No');
    if (dataE.participants.vulnerableGroups.includes) {
      addField('Vulnerable Groups', dataE.participants.vulnerableGroups.types?.join(', '));
      addField('Justification & Safeguards', dataE.participants.vulnerableGroups.justification);
    }

    addSection('4. Recruitment & Consent');
    addField('Recruitment Methods', dataE.recruitment.methods?.join(', '));
    addField('Initial Contact Information', dataE.recruitment.initialInfo);
    addField('Consent Process', dataE.recruitment.consentProcess);
    addField('Withdrawal Procedure', dataE.recruitment.withdrawalProcedure);
    addField('Debrief Procedure', dataE.recruitment.debriefProcedure);

    addSection('5. Data Collection');
    addField('Collection Methods', dataE.dataCollection.methods?.join(', '));
    addField('Research Instruments', dataE.dataCollection.instruments);
    addField('Pilot Testing', dataE.dataCollection.pilotTesting);

    addSection('6. Data Management');
    addField('Types of Personal Data', dataE.dataManagement.dataTypes?.join(', '));
    addField('Recording Method', dataE.dataManagement.recordingMethod);
    addField('Anonymization Method', dataE.dataManagement.anonymizationMethod);
    addField('Storage Locations', dataE.dataManagement.storageLocations?.join(', '));
    addField('Access Control', dataE.dataManagement.accessControl);
    addField('Security Measures', dataE.dataManagement.securityMeasures);
    addField('Retention Period', getLabel(dataE.dataManagement.retentionPeriod, retentionPeriods));
    addField('Disposal Method', dataE.dataManagement.disposalMethod);
    addField('GDPR Compliant', dataE.dataManagement.gdprCompliant ? 'Yes' : 'No');

    addSection('7. Risk Assessment');
    addField('Participant Risks', dataE.riskAssessment.participantRisks?.join(', '));
    addField('Mitigation Measures', dataE.riskAssessment.participantMitigations);
    addField('Researcher Risks', dataE.riskAssessment.researcherRisks);
    addField('Researcher Mitigations', dataE.riskAssessment.researcherMitigations);
    addField('Incident Response', dataE.riskAssessment.incidentResponse);

    addSection('8. Special Considerations');
    if (dataE.specialConsiderations.involvesMinors) {
      addField('Involves Children/Young People', dataE.specialConsiderations.minorsDetails);
    }
    if (dataE.specialConsiderations.involvesHumanTissue) {
      addField('Involves Human Tissue', dataE.specialConsiderations.tissueDetails);
    }
    if (dataE.specialConsiderations.involvesIncentives) {
      addField('Participant Incentives', dataE.specialConsiderations.incentivesDetails);
    }
    if (!dataE.specialConsiderations.involvesMinors && 
        !dataE.specialConsiderations.involvesHumanTissue && 
        !dataE.specialConsiderations.involvesIncentives) {
      addLine('No special considerations apply.');
    }

    addSection('9. Dissemination');
    addField('Dissemination Methods', dataE.dissemination.methods?.join(', '));
    addField('Data Sharing Plans', dataE.dissemination.dataSharing);
    addField('Embargo Requirements', dataE.dissemination.embargoPlans);
    addField('Participant Feedback', dataE.dissemination.participantFeedback);

    addSection('10. Declarations');
    addField('Accuracy Confirmed', dataE.declarations.confirmedAccuracy ? 'Yes' : 'No');
    addField('GDPR Compliance Confirmed', dataE.declarations.confirmedGDPR ? 'Yes' : 'No');
    addField('Training Confirmed', dataE.declarations.confirmedTraining ? 'Yes' : 'No');
    addField('Additional Comments', dataE.declarations.additionalComments);

    addLine('');
    addLine('-'.repeat(50));
    addLine(`Generated on ${new Date().toLocaleDateString('en-GB')} using Research Methodology Wizard`);

    return lines.join('\n');
  };

  const handleCopy = async () => {
    const text = generatePlainText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleBackToWizard = () => {
    goToStep(9);
  };

  return (
    <div className="ethics-report">
      <div className="report-actions no-print">
        <button className="btn-secondary" onClick={handleBackToWizard}>
          <ArrowLeft size={18} /> Back to Edit
        </button>
        <div className="action-group">
          <button className="btn-primary" onClick={generatePDF}>
            <Download size={18} /> Download PDF
          </button>
          <button className="btn-secondary" onClick={handleCopy}>
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? 'Copied!' : 'Copy Text'}
          </button>
          <button className="btn-secondary" onClick={handlePrint}>
            <Printer size={18} /> Print
          </button>
        </div>
      </div>

      <div className="report-content" ref={reportRef}>
        <div className="report-header">
          <FileText size={40} />
          <div>
            <h1>Ethics Application</h1>
            <h2>{dataE.projectSynopsis.title || 'Untitled Project'}</h2>
          </div>
        </div>

        <div className="completion-banner">
          <CheckCircle size={24} />
          <span>Ethics wizard completed successfully</span>
        </div>

        <section className="report-section">
          <h3>1. Research Team</h3>
          <div className="field-grid">
            <div className="field">
              <label>Principal Investigator</label>
              <p>{dataE.researchTeam.principalInvestigator.firstName} {dataE.researchTeam.principalInvestigator.lastName}</p>
            </div>
            <div className="field">
              <label>Email</label>
              <p>{dataE.researchTeam.principalInvestigator.email}</p>
            </div>
            <div className="field">
              <label>Institution</label>
              <p>{dataE.researchTeam.principalInvestigator.institution}</p>
            </div>
            <div className="field">
              <label>Role</label>
              <p>{getLabel(dataE.researchTeam.principalInvestigator.role, roles)}</p>
            </div>
          </div>
          {dataE.researchTeam.supervisors.length > 0 && (
            <div className="field">
              <label>Supervisors</label>
              <ul>
                {dataE.researchTeam.supervisors.map((s, i) => (
                  <li key={i}>{s.firstName} {s.lastName} ({s.email})</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="report-section">
          <h3>2. Project Synopsis</h3>
          <div className="field">
            <label>Project Title</label>
            <p>{dataE.projectSynopsis.title}</p>
          </div>
          <div className="field-grid">
            <div className="field">
              <label>Project Dates</label>
              <p>{formatDate(dataE.projectSynopsis.startDate)} - {formatDate(dataE.projectSynopsis.endDate)}</p>
            </div>
            <div className="field">
              <label>Ethics Period</label>
              <p>{formatDate(dataE.projectSynopsis.ethicsStartDate)} - {formatDate(dataE.projectSynopsis.ethicsEndDate)}</p>
            </div>
          </div>
          <div className="field">
            <label>Project Summary</label>
            <p className="pre-wrap">{dataE.projectSynopsis.summary}</p>
          </div>
          <div className="field">
            <label>Research Questions</label>
            <p className="pre-wrap">{dataE.projectSynopsis.researchQuestions}</p>
          </div>
          <div className="field">
            <label>Methodology</label>
            <p className="pre-wrap">{dataE.projectSynopsis.methodology}</p>
          </div>
        </section>

        <section className="report-section">
          <h3>3. Participants</h3>
          <div className="field">
            <label>Description</label>
            <p className="pre-wrap">{dataE.participants.description}</p>
          </div>
          <div className="field">
            <label>Inclusion Criteria</label>
            <p className="pre-wrap">{dataE.participants.inclusionCriteria}</p>
          </div>
          <div className="field">
            <label>Exclusion Criteria</label>
            <p className="pre-wrap">{dataE.participants.exclusionCriteria || 'Not specified'}</p>
          </div>
          {dataE.participants.vulnerableGroups.includes && (
            <div className="field warning-field">
              <label>Vulnerable Groups</label>
              <p>{dataE.participants.vulnerableGroups.types?.join(', ')}</p>
              <p className="pre-wrap">{dataE.participants.vulnerableGroups.justification}</p>
            </div>
          )}
        </section>

        <section className="report-section">
          <h3>4. Recruitment & Consent</h3>
          <div className="field">
            <label>Recruitment Methods</label>
            <p>{dataE.recruitment.methods?.join(', ') || 'Not specified'}</p>
          </div>
          <div className="field">
            <label>Consent Process</label>
            <p className="pre-wrap">{dataE.recruitment.consentProcess}</p>
          </div>
          <div className="field">
            <label>Withdrawal Procedure</label>
            <p className="pre-wrap">{dataE.recruitment.withdrawalProcedure}</p>
          </div>
        </section>

        <section className="report-section">
          <h3>5. Data Collection</h3>
          <div className="field">
            <label>Collection Methods</label>
            <p>{dataE.dataCollection.methods?.join(', ') || 'Not specified'}</p>
          </div>
          <div className="field">
            <label>Research Instruments</label>
            <p className="pre-wrap">{dataE.dataCollection.instruments}</p>
          </div>
        </section>

        <section className="report-section">
          <h3>6. Data Management</h3>
          <div className="field">
            <label>Types of Personal Data</label>
            <p>{dataE.dataManagement.dataTypes?.join(', ') || 'Not specified'}</p>
          </div>
          <div className="field">
            <label>Anonymization Method</label>
            <p className="pre-wrap">{dataE.dataManagement.anonymizationMethod}</p>
          </div>
          <div className="field">
            <label>Storage Locations</label>
            <p>{dataE.dataManagement.storageLocations?.join(', ') || 'Not specified'}</p>
          </div>
          <div className="field-grid">
            <div className="field">
              <label>Retention Period</label>
              <p>{getLabel(dataE.dataManagement.retentionPeriod, retentionPeriods)}</p>
            </div>
            <div className="field">
              <label>GDPR Compliant</label>
              <p>{dataE.dataManagement.gdprCompliant ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </section>

        <section className="report-section">
          <h3>7. Risk Assessment</h3>
          <div className="field">
            <label>Participant Risks</label>
            <p>{dataE.riskAssessment.participantRisks?.join(', ') || 'None identified'}</p>
          </div>
          <div className="field">
            <label>Mitigation Measures</label>
            <p className="pre-wrap">{dataE.riskAssessment.participantMitigations || 'Not applicable'}</p>
          </div>
        </section>

        <section className="report-section">
          <h3>8. Special Considerations</h3>
          {!dataE.specialConsiderations.involvesMinors && 
           !dataE.specialConsiderations.involvesHumanTissue && 
           !dataE.specialConsiderations.involvesIncentives &&
           !dataE.specialConsiderations.involvesClinical &&
           !dataE.specialConsiderations.involvesInternationalData &&
           !dataE.specialConsiderations.securitySensitive ? (
            <p>No special considerations apply to this research.</p>
          ) : (
            <>
              {dataE.specialConsiderations.involvesMinors && (
                <div className="field warning-field">
                  <label>Children/Young People</label>
                  <p className="pre-wrap">{dataE.specialConsiderations.minorsDetails}</p>
                </div>
              )}
              {dataE.specialConsiderations.involvesIncentives && (
                <div className="field">
                  <label>Participant Incentives</label>
                  <p className="pre-wrap">{dataE.specialConsiderations.incentivesDetails}</p>
                </div>
              )}
            </>
          )}
        </section>

        <section className="report-section">
          <h3>9. Dissemination</h3>
          <div className="field">
            <label>Dissemination Methods</label>
            <p>{dataE.dissemination.methods?.join(', ') || 'Not specified'}</p>
          </div>
          <div className="field">
            <label>Data Sharing Plans</label>
            <p className="pre-wrap">{dataE.dissemination.dataSharing || 'Not specified'}</p>
          </div>
        </section>

        <section className="report-section declarations-section">
          <h3>10. Declarations</h3>
          <div className="declaration-status">
            <div className={`declaration ${dataE.declarations.confirmedAccuracy ? 'confirmed' : ''}`}>
              <CheckCircle size={18} />
              <span>Information accuracy confirmed</span>
            </div>
            <div className={`declaration ${dataE.declarations.confirmedGDPR ? 'confirmed' : ''}`}>
              <CheckCircle size={18} />
              <span>GDPR compliance confirmed</span>
            </div>
            <div className={`declaration ${dataE.declarations.confirmedTraining ? 'confirmed' : ''}`}>
              <CheckCircle size={18} />
              <span>Training completion confirmed</span>
            </div>
          </div>
          {dataE.declarations.additionalComments && (
            <div className="field">
              <label>Additional Comments</label>
              <p className="pre-wrap">{dataE.declarations.additionalComments}</p>
            </div>
          )}
        </section>

        <div className="report-footer">
          <p>Generated on {new Date().toLocaleDateString('en-GB')} using Research Methodology Wizard</p>
        </div>
      </div>
    </div>
  );
}

export default EthicsReport;
